import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// Local types for the Supabase-based implementation
export interface Payment {
  id: string
  organization_id: string
  organization_name: string | undefined
  subscription_id: string | null
  amount: number
  currency: string
  status: string
  payment_method: string | null
  stripe_payment_intent_id: string | null
  description: string | null
  refunded_amount: number
  refunded_at: string | null
  created_at: string
  updated_at: string
}

interface PaymentsResponse {
  data: Payment[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

interface PaymentStats {
  total_revenue: number
  total_refunds: number
  net_revenue: number
  payment_count: number
  successful_payments: number
  failed_payments: number
  refund_count: number
}

interface ListPaymentsParams {
  page?: number
  page_size?: number
  status?: string
  organization_id?: string
  start_date?: string
  end_date?: string
}

export function usePayments(params: ListPaymentsParams = {}) {
  const { page = 1, page_size = 20, status, organization_id, start_date, end_date } = params

  return useQuery({
    queryKey: ['super-admin', 'payments', params],
    queryFn: async (): Promise<PaymentsResponse> => {
      let query = supabase
        .from('payments')
        .select('*, organizations(name)', { count: 'exact' })
        .order('created_at', { ascending: false })

      if (status) query = query.eq('status', status)
      if (organization_id) query = query.eq('organization_id', organization_id)
      if (start_date) query = query.gte('created_at', start_date)
      if (end_date) query = query.lte('created_at', end_date)

      // Pagination
      const from = (page - 1) * page_size
      const to = from + page_size - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      const payments: Payment[] = (data || []).map((payment: any) => ({
        id: payment.id,
        organization_id: payment.organization_id,
        organization_name: payment.organizations?.name,
        subscription_id: payment.subscription_id,
        amount: payment.amount,
        currency: payment.currency || 'BRL',
        status: payment.status,
        payment_method: payment.payment_method,
        stripe_payment_intent_id: payment.stripe_payment_intent_id,
        description: payment.description,
        refunded_amount: payment.refunded_amount || 0,
        refunded_at: payment.refunded_at,
        created_at: payment.created_at,
        updated_at: payment.updated_at,
      }))

      const total = count || 0
      const total_pages = Math.ceil(total / page_size)

      return {
        data: payments,
        pagination: { page, page_size, total, total_pages },
      }
    },
  })
}

export function usePayment(id: string) {
  return useQuery({
    queryKey: ['super-admin', 'payment', id],
    queryFn: async (): Promise<Payment> => {
      const { data: payment, error } = await supabase
        .from('payments')
        .select('*, organizations(name)')
        .eq('id', id)
        .single()

      if (error) throw error

      return {
        id: payment.id,
        organization_id: payment.organization_id,
        organization_name: payment.organizations?.name,
        subscription_id: payment.subscription_id,
        amount: payment.amount,
        currency: payment.currency || 'BRL',
        status: payment.status,
        payment_method: payment.payment_method,
        stripe_payment_intent_id: payment.stripe_payment_intent_id,
        description: payment.description,
        refunded_amount: payment.refunded_amount || 0,
        refunded_at: payment.refunded_at,
        created_at: payment.created_at,
        updated_at: payment.updated_at,
      }
    },
    enabled: !!id,
  })
}

export function useOrganizationPayments(organizationId: string, limit?: number) {
  return useQuery({
    queryKey: ['super-admin', 'payments', 'organization', organizationId, limit],
    queryFn: async (): Promise<Payment[]> => {
      let query = supabase
        .from('payments')
        .select('*, organizations(name)')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (limit) {
        query = query.limit(limit)
      }

      const { data, error } = await query

      if (error) throw error

      return (data || []).map((payment: any) => ({
        id: payment.id,
        organization_id: payment.organization_id,
        organization_name: payment.organizations?.name,
        subscription_id: payment.subscription_id,
        amount: payment.amount,
        currency: payment.currency || 'BRL',
        status: payment.status,
        payment_method: payment.payment_method,
        stripe_payment_intent_id: payment.stripe_payment_intent_id,
        description: payment.description,
        refunded_amount: payment.refunded_amount || 0,
        refunded_at: payment.refunded_at,
        created_at: payment.created_at,
        updated_at: payment.updated_at,
      }))
    },
    enabled: !!organizationId,
  })
}

export function usePaymentStats(organizationId?: string) {
  return useQuery({
    queryKey: ['super-admin', 'payments', 'stats', organizationId],
    queryFn: async (): Promise<PaymentStats> => {
      let query = supabase.from('payments').select('amount, status, refunded_amount')

      if (organizationId) {
        query = query.eq('organization_id', organizationId)
      }

      const { data, error } = await query

      if (error) throw error

      const payments = data || []

      const stats = payments.reduce(
        (acc, payment) => {
          acc.payment_count++

          if (payment.status === 'succeeded' || payment.status === 'paid') {
            acc.successful_payments++
            acc.total_revenue += payment.amount || 0
          } else if (payment.status === 'failed') {
            acc.failed_payments++
          }

          if (payment.refunded_amount && payment.refunded_amount > 0) {
            acc.refund_count++
            acc.total_refunds += payment.refunded_amount
          }

          return acc
        },
        {
          total_revenue: 0,
          total_refunds: 0,
          net_revenue: 0,
          payment_count: 0,
          successful_payments: 0,
          failed_payments: 0,
          refund_count: 0,
        }
      )

      stats.net_revenue = stats.total_revenue - stats.total_refunds

      return stats
    },
  })
}

export function useRefundPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, amount }: { id: string; amount?: number }): Promise<Payment> => {
      // Get current payment
      const { data: current, error: fetchError } = await supabase
        .from('payments')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      const refundAmount = amount || current.amount
      const totalRefunded = (current.refunded_amount || 0) + refundAmount

      const { data: payment, error } = await supabase
        .from('payments')
        .update({
          status: totalRefunded >= current.amount ? 'refunded' : 'partially_refunded',
          refunded_amount: totalRefunded,
          refunded_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      return payment as Payment
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'payments'] })
    },
  })
}
